import { Injectable } from "@angular/core";
import { BaseService } from "../../core";
import { environment } from "environments/environment";
import { HttpClient } from "@angular/common/http";
import { ComunicadoResponse, MarcarVisualizadoResponse } from "app/shared/models/ei-models/comunicado/_index";
import { BehaviorSubject, Observable, tap, catchError, of, map } from "rxjs";

@Injectable({ providedIn: 'root' })
export class ComunicadoService extends BaseService {
  private controllerEndpoint = environment.urlEdducaImobApi.concat('api/Comunicado/');
  
  // BehaviorSubject para manter o estado reativo da contagem de não lidos
  private _unreadCount$ = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this._unreadCount$.asObservable();

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Atualiza a contagem de comunicados não lidos
   */
  refreshUnreadCount(usuarioId: number): void {
    if (!usuarioId) return;
    
    this.getUnreadCount(usuarioId).subscribe({
      next: (count) => this._unreadCount$.next(count),
      error: () => this._unreadCount$.next(0)
    });
  }

  /**
   * Busca a contagem de comunicados não lidos para o usuário
   */
  getUnreadCount(usuarioId: number): Observable<number> {
    return this.httpClient.get<number>(
      `${this.controllerEndpoint}UnreadCount/${usuarioId}`,
      { headers: this.getHeader(false) }
    ).pipe(
      catchError(() => of(0))
    );
  }

  /**
   * Lista comunicados recebidos pelo usuário (para visualização)
   */
  listarRecebidos(usuarioId: number) {
    return this.httpClient.get<ComunicadoResponse[]>(
      `${this.controllerEndpoint}ListarRecebidos/${usuarioId}`,
      { headers: this.getHeader(true) }
    );
  }

  /**
   * Lista comunicados enviados pelo usuário (para ADM_IMOBILIARIA)
   */
  listarEnviados(usuarioId: number) {
    return this.httpClient.get<ComunicadoResponse[]>(
      `${this.controllerEndpoint}ListarEnviados/${usuarioId}`,
      { headers: this.getHeader(true) }
    );
  }

  /**
   * Lista todos os comunicados de uma imobiliária (para ADM_SISTEMA e PARCEIRO)
   */
  listarPorImobiliaria(imobiliariaId: number) {
    return this.httpClient.get<ComunicadoResponse[]>(
      `${this.controllerEndpoint}ListarPorImobiliaria/${imobiliariaId}`,
      { headers: this.getHeader(true) }
    );
  }

  /**
   * @deprecated Use listarRecebidos ou listarPorImobiliaria
   */
  listarPorImobiliariaUsuario(imobiliariaId: number, usuarioId: number) {
    return this.httpClient.get<ComunicadoResponse[]>(
      `${this.controllerEndpoint}ListarPorImobiliaria/${imobiliariaId}/Usuario/${usuarioId}`,
      { headers: this.getHeader(true) }
    );
  }

  /**
   * @deprecated Use getUnreadCount(usuarioId)
   */
  unreadCount(imobiliariaId: number, usuarioId: number) {
    return this.httpClient.get<number>(
      `${this.controllerEndpoint}UnreadCount/${imobiliariaId}/Usuario/${usuarioId}`
    );
  }

  /**
   * Marca um comunicado como visualizado e atualiza a contagem
   * Retorna dados de gamificação se houver pontos ganhos
   */
  marcarVisualizado(comunicadoId: number, usuarioId: number): Observable<MarcarVisualizadoResponse> {
    return this.httpClient.post<MarcarVisualizadoResponse>(
      `${this.controllerEndpoint}MarcarVisualizado/${comunicadoId}/Usuario/${usuarioId}`,
      {}, { headers: this.getHeader(true) }
    ).pipe(
      tap(() => this.refreshUnreadCount(usuarioId))
    );
  }

  /**
   * Cria um novo comunicado
   */
  criarComunicado(payload: any) {
    return this.httpClient.post<ComunicadoResponse>(
      `${this.controllerEndpoint}CriarComunicado`,
      payload, { headers: this.getHeader(true) }
    );
  }

  /**
   * Decrementa manualmente a contagem (para uso local sem chamar API)
   */
  decrementUnreadCount(): void {
    const current = this._unreadCount$.value;
    if (current > 0) {
      this._unreadCount$.next(current - 1);
    }
  }
}